import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';

import { apiClient } from '../api/client';
import { useApi } from '../api/useApi';
import { onConsentGiven } from '../cookie-consent';
import type { TreasureHunt, TreasureHuntIcon } from '../types/api';

import { TreasureClaim } from './TreasureClaim';
import { TreasureHuntIntro } from './TreasureHuntIntro';

export interface CaughtIcon {
  icon: TreasureHuntIcon;
  standId: number;
}

interface TreasureHuntContextValue {
  /** Null while loading, on failure, and on fairs that do not run the giveaway. */
  hunt: TreasureHunt | null;
  /** Opens the entry form for an icon the visitor just caught. */
  claim: (caught: CaughtIcon) => void;
  /** The visitor has entered the draw from this browser; no more icons drop. */
  entered: boolean;
}

const TreasureHuntContext = createContext<TreasureHuntContextValue | undefined>(undefined);

/** Set once the intro has been shown; each fair's domain has its own storage. */
const INTRO_SEEN_KEY = 'vv.treasureHuntIntroSeen';
/**
 * Set once the visitor has entered the draw. Kept in the browser: the legacy
 * API has no way to ask whether someone already entered.
 */
const ENTERED_KEY = 'vv.treasureHuntEntered';

function isFlagSet(key: string): boolean {
  try {
    return window.localStorage.getItem(key) !== null;
  } catch {
    // Storage blocked: the visitor sees the intro again, and icons keep
    // dropping, which beats never seeing either.
    return false;
  }
}

function setFlag(key: string): void {
  try {
    window.localStorage.setItem(key, '1');
  } catch {
    // As above.
  }
}

/**
 * "Lov na blago", the legacy giveaway, switched on per fair in the
 * `treasure_hunt` table. Held above the routes: the intro opens on the first
 * visit whatever the landing page, and the entry form must survive the visitor
 * moving on while it is open.
 */
export function TreasureHuntProvider({ children }: { children: ReactNode }): ReactElement {
  const request = useApi(() => apiClient.getTreasureHunt());
  const hunt = request.data ?? null;
  const [introOpen, setIntroOpen] = useState(false);
  const [caught, setCaught] = useState<CaughtIcon>();
  const [entered, setEntered] = useState(() => isFlagSet(ENTERED_KEY));

  const closeIntro = useCallback((): void => {
    setIntroOpen(false);
  }, []);

  const closeClaim = useCallback((): void => {
    setCaught(undefined);
  }, []);

  const markEntered = useCallback((): void => {
    setFlag(ENTERED_KEY);
    setEntered(true);
  }, []);

  const enabled = hunt !== null;

  // Once per visitor, after the cookie choice, so it never opens on top of the
  // banner. Marked seen on opening, so a reload while it is up does not repeat it.
  useEffect(() => {
    if (!enabled || isFlagSet(INTRO_SEEN_KEY)) {
      return undefined;
    }

    return onConsentGiven(() => {
      setFlag(INTRO_SEEN_KEY);
      setIntroOpen(true);
    });
  }, [enabled]);

  const value = useMemo(() => ({ hunt, claim: setCaught, entered }), [hunt, entered]);

  return (
    <TreasureHuntContext.Provider value={value}>
      {children}
      {hunt !== null && <TreasureHuntIntro hunt={hunt} open={introOpen} onClose={closeIntro} />}
      {caught !== undefined && (
        // Keyed so catching another icon starts a fresh form.
        <TreasureClaim
          key={`${String(caught.icon.id)}:${String(caught.standId)}`}
          caught={caught}
          question={hunt?.question ?? null}
          onEntered={markEntered}
          onClose={closeClaim}
        />
      )}
    </TreasureHuntContext.Provider>
  );
}

export function useTreasureHunt(): TreasureHuntContextValue {
  const context = useContext(TreasureHuntContext);

  if (context === undefined) {
    throw new Error('useTreasureHunt must be used inside <TreasureHuntProvider>');
  }

  return context;
}
